import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { TextInput } from "@/components/admin/text-input";
import { TextField } from "@/components/admin/text-field";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useListContext, useRecordContext } from "ra-core";
import type { Vendor } from "../types";

const StatusCell = () => {
    const record = useRecordContext<Vendor>();
    if (!record) return null;

    const variants: Record<string, "outline" | "default" | "secondary" | "destructive"> = {
        approved: "default",
        conditional: "default",
        checked: "secondary",
        on_hold: "secondary",
        pending_approval: "secondary",
        rejected: "destructive",
        blacklisted: "outline",
        quick_vendor: "secondary",
    };

    const labels: Record<string, string> = {
        approved: "Approved",
        conditional: "Conditional",
        checked: "Checked",
        on_hold: "On Hold",
        pending_approval: "Pending Approval",
        rejected: "Rejected",
        blacklisted: "Blacklisted",
        quick_vendor: "Quick Vendor",
    };

    return (
        <Badge variant={variants[record.status] || "outline"}>
            {labels[record.status] || record.status}
        </Badge>
    );
};

const VendorAvatar = () => {
    const record = useRecordContext();
    if (!record) return null;

    const name = record.name || '';
    const initials = name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((n: string) => n[0])
        .join('')
        .toUpperCase() || 'V';

    const colors = [
        'bg-orange-100 text-orange-700',
        'bg-blue-100 text-blue-700',
        'bg-green-100 text-green-700',
        'bg-purple-100 text-purple-700',
        'bg-rose-100 text-rose-700',
        'bg-cyan-100 text-cyan-700',
        'bg-amber-100 text-amber-700',
        'bg-teal-100 text-teal-700'
    ];
    const charCodeSum = initials.split('').reduce((sum: number, char: string) => sum + char.charCodeAt(0), 0);
    const colorClass = colors[charCodeSum % colors.length];

    return (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${colorClass}`}>
            {initials}
        </div>
    );
};

const VendorStatusTabsFilter = () => {
    const { filterValues, setFilters } = useListContext();
    const currentStatus = filterValues.status;
    const value = currentStatus === 'pending_approval' ? 'pending' : 'all';

    const handleChange = (val: string) => {
        setFilters(
            {
                ...filterValues,
                status: val === 'pending' ? 'pending_approval' : undefined
            },
            undefined,
            true
        );
    };

    return (
        <div className="flex relative mt-auto">
            <Tabs value={value} onValueChange={handleChange} className="w-fit">
                <TabsList className="h-9">
                    <TabsTrigger value="all" className="text-xs px-3">All Vendors</TabsTrigger>
                    <TabsTrigger value="pending" className="text-xs px-3">Approval Queue</TabsTrigger>
                </TabsList>
            </Tabs>
        </div>
    );
};

const vendorFilters = [
    <SearchInput source="q" alwaysOn />,
    <VendorStatusTabsFilter alwaysOn />,
    <TextInput source="category" label="Category" />,
];

const VendorListActions = () => (
    <div className="flex items-center gap-2">
        <CreateButton label="Add New Vendor" />
        <ExportButton />
    </div>
);

const VendorTable = () => (
    <DataTable rowClassName={() => "hover:bg-primary/5 transition-colors odd:bg-muted/40"}>
        <DataTable.Col label="NAME" cellClassName="py-3 px-4">
            <div className="flex items-center gap-3">
                <VendorAvatar />
                <span className="font-semibold text-slate-900 text-sm">
                    <TextField source="name" />
                </span>
            </div>
        </DataTable.Col>
        
        <DataTable.Col label="CONTACT PERSON" cellClassName="hidden md:table-cell py-3 px-4" headerClassName="hidden md:table-cell">
            <span className="font-medium text-slate-700 text-sm">
                <TextField source="contact_person" empty="-" />
            </span>
        </DataTable.Col>

        <DataTable.Col source="category" label="CATEGORY" cellClassName="hidden sm:table-cell py-3 px-4 text-slate-600 font-medium text-sm" headerClassName="hidden sm:table-cell" />

        <DataTable.Col source="phone" label="PHONE" cellClassName="hidden lg:table-cell py-3 px-4 text-slate-700 font-medium text-sm" headerClassName="hidden lg:table-cell" />

        <DataTable.Col source="email" label="EMAIL" cellClassName="hidden md:table-cell py-3 px-4 text-slate-600 font-medium text-sm" headerClassName="hidden md:table-cell" />

        <DataTable.Col label="STATUS" cellClassName="py-3 px-4">
            <StatusCell />
        </DataTable.Col>
    </DataTable>
);

export const VendorList = () => {
    return (
        <List title="Vendors" filters={vendorFilters} actions={<VendorListActions />}>
            <VendorTable />
        </List>
    );
};
